import React from 'react';
import { Users, Award, BadgeCheck } from 'lucide-react';

const BADGE_CONFIG = {
  ambassadeur: {
    name: 'Ambassadeur',
    description: 'A parrainé au moins 1 personne',
    icon: Users,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    iconBg: 'bg-blue-500'
  },
  top_ambassadeur: {
    name: 'Top Ambassadeur',
    description: 'A parrainé au moins 10 personnes',
    icon: Award,
    bgColor: 'bg-gradient-to-r from-yellow-100 to-orange-100',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
    iconBg: 'bg-gradient-to-r from-yellow-400 to-orange-500'
  },
  expert_verifie: {
    name: 'Expert Vérifié',
    description: 'Identité et compétences vérifiées',
    icon: BadgeCheck,
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
    iconBg: 'bg-purple-500'
  }
};

// Small badge for inline display
export const BadgeSmall = ({ badgeId }) => {
  const config = BADGE_CONFIG[badgeId];
  if (!config) return null;
  
  const Icon = config.icon;
  
  return (
    <div 
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor} border ${config.borderColor}`}
      title={config.description}
    >
      <Icon className="h-3 w-3" />
      {config.name}
    </div>
  );
};

// Badge icon only (for compact display)
export const BadgeIcon = ({ badgeId, size = 'md' }) => {
  const config = BADGE_CONFIG[badgeId];
  if (!config) return null;
  
  const Icon = config.icon;
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };
  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} ${config.iconBg} rounded-full flex items-center justify-center text-white shadow-sm`}
      title={`${config.name}: ${config.description}`}
    >
      <Icon className={iconSizes[size]} />
    </div>
  );
};

// Full badge card for detailed display
export const BadgeCard = ({ badgeId }) => {
  const config = BADGE_CONFIG[badgeId];
  if (!config) return null;
  
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl ${config.bgColor} border ${config.borderColor}`}>
      <div className={`w-10 h-10 ${config.iconBg} rounded-full flex items-center justify-center text-white`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className={`font-semibold ${config.textColor}`}>{config.name}</p>
        <p className="text-xs text-gray-500">{config.description}</p>
      </div>
    </div>
  );
};

// Badge list component
const UserBadges = ({ badges = [], display = 'icons', maxDisplay = 3 }) => {
  if (!badges || badges.length === 0) return null;
  
  const displayedBadges = badges.slice(0, maxDisplay);
  const remainingCount = badges.length - maxDisplay;
  
  if (display === 'icons') {
    return (
      <div className="flex items-center gap-1">
        {displayedBadges.map((badgeId) => (
          <BadgeIcon key={badgeId} badgeId={badgeId} size="sm" />
        ))}
        {remainingCount > 0 && (
          <span className="text-xs text-gray-500 ml-1">+{remainingCount}</span>
        )}
      </div>
    );
  }
  
  if (display === 'small') {
    return (
      <div className="flex flex-wrap gap-1">
        {displayedBadges.map((badgeId) => (
          <BadgeSmall key={badgeId} badgeId={badgeId} />
        ))}
        {remainingCount > 0 && (
          <span className="text-xs text-gray-500 px-2 py-0.5">+{remainingCount}</span>
        )}
      </div>
    );
  }
  
  if (display === 'full') {
    return (
      <div className="space-y-2">
        {badges.map((badgeId) => (
          <BadgeCard key={badgeId} badgeId={badgeId} />
        ))}
      </div>
    );
  }
  
  return null;
};

export default UserBadges;
